import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
  Paper,
  alpha,
  Avatar,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { openNotification } from '../../../../helpers/notification';
import LoaderSkeleton from '../../../common/LoaderSkeleton';
import { getFacilitiyConsultants } from '../../../../helpers/api';
import { Empty } from 'antd';
import MUIDataTable from 'mui-datatables';
import {
  Add,
  ApprovalRounded,
  MoreHoriz,
  VisibilityRounded,
  BusinessCenterRounded,
  EmailOutlined,
  PhoneIphoneRounded,
  KeyboardArrowRightRounded,
} from '@mui/icons-material';
import ConsultantInvite from './ConsultantInvite';
import ConsultantRemove from './ConsultantRemove';
import { useHistory } from 'react-router-dom';
import { userCats } from '../../../../helpers/appstate';
import ConsultantHandleRequest from './ConsultantHandleRequest';

export default function FacilityConsultants({ props, facilityId }) {
  const { userCategory } = props;
  const history = useHistory();
  const [consultantList, setConsultantList] = useState([]);
  const [consultantLoading, setConsultantLoading] = useState(false);
  const [consultantSelected, setConsultantSelected] = useState();
  const [dialogInvite, setDialogInvite] = useState(false);
  const [dialogRemove, setDialogRemove] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [dialogRequest, setDialogRequest] = useState(false);
  const [refresh, setRefresh] = useState(false);

  useEffect(() => {
    handleFetchFacilityItem();
  }, [facilityId]);

  useEffect(() => {
    if (refresh) handleFetchFacilityItem();
  }, [refresh]);

  const handleMenuOpen = (event, record) => {
    setConsultantSelected(record);
    setMenuAnchor(event.currentTarget);
  };
  const handleMenuClose = () => setMenuAnchor(null);

  const handleFetchFacilityItem = async () => {
    setConsultantLoading(true);
    try {
      const res = await getFacilitiyConsultants(facilityId);
      const { data, status } = res?.result;
      if (status?.code === '00') {
        setConsultantList(data || []);
      } else {
        openNotification({ type: 'error', message: status?.desc });
      }
    } catch (error) {
      openNotification({ type: 'error', message: 'Unable to fetch consultant list.' });
    } finally {
      setConsultantLoading(false);
      setRefresh(false);
    }
  };

  const compactCell = { style: { padding: '10px 14px' } };

  const renderContent = () => {
    const columns = [
      {
        name: 'consultantName',
        label: 'Consultancy Firm',
        options: {
          setCellProps: () => compactCell,
          customHeadLabelRender: (a) => (
            <Typography variant="caption" fontWeight={700} color="textSecondary">
              {a.label}
            </Typography>
          ),
          customBodyRenderLite: (index) => {
            const c = consultantList[index];
            return (
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar
                  sx={{
                    bgcolor: alpha('#1976d2', 0.1),
                    color: 'primary.main',
                    width: 34,
                    height: 34,
                  }}
                >
                  <BusinessCenterRounded sx={{ fontSize: 18 }} />
                </Avatar>
                <Typography variant="body2" fontWeight={700} color="text.primary">
                  {c?.consultantName}
                </Typography>
              </Stack>
            );
          },
        },
      },
      {
        name: 'consultantEmail',
        label: 'Contact Details',
        options: {
          setCellProps: () => compactCell,
          customHeadLabelRender: (a) => (
            <Typography variant="caption" fontWeight={700} color="textSecondary">
              {a.label}
            </Typography>
          ),
          customBodyRenderLite: (index) => (
            <Box>
              <Typography
                variant="body2"
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.2 }}
              >
                <EmailOutlined sx={{ fontSize: 14, color: 'text.disabled' }} />{' '}
                {consultantList[index]?.consultantEmail}
              </Typography>
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
              >
                <PhoneIphoneRounded sx={{ fontSize: 12, color: 'text.disabled' }} />{' '}
                {consultantList[index]?.consultantPhone}
              </Typography>
            </Box>
          ),
        },
      },
      {
        name: 'status',
        label: 'Affiliation Status',
        options: {
          setCellProps: () => compactCell,
          customHeadLabelRender: (a) => (
            <Typography variant="caption" fontWeight={700} color="textSecondary">
              {a.label}
            </Typography>
          ),
          customBodyRenderLite: (index) => {
            const item = consultantList[index];
            let label = 'Active';
            let color = '#2e7d32';

            if (item.status === 'PENDING') {
              if (item.inducedBy === 'CONSULTANT') {
                label = 'Pending Approval';
                color = '#ed6c02';
              } else {
                label = 'Invite Sent';
                color = '#0288d1';
              }
            } else if (item.status === 'DEACTIVATED' || item.status === 'DECLINED') {
              label = item.status;
              color = '#d32f2f';
            }

            return (
              <Chip
                label={label}
                size="small"
                sx={{
                  height: 22,
                  fontWeight: 600,
                  bgcolor: alpha(color, 0.1),
                  color: color,
                  border: '1px solid',
                  borderColor: alpha(color, 0.2),
                }}
              />
            );
          },
        },
      },
      {
        name: 'action',
        label: 'Action',
        options: {
          setCellProps: () => ({ style: { padding: '10px 14px', textAlign: 'center' } }),
          customHeadLabelRender: (a) => (
            <Typography variant="caption" fontWeight={700} color="textSecondary" align="center">
              {a.label}
            </Typography>
          ),
          customBodyRenderLite: (index) => {
            const record = consultantList[index];
            if (userCategory === userCats.PERSONNEL) {
              return (
                <IconButton size="small" onClick={(e) => handleMenuOpen(e, record)}>
                  <MoreHoriz fontSize="small" />
                </IconButton>
              );
            }
            return (
              <Button
                variant="text"
                size="small"
                endIcon={<KeyboardArrowRightRounded />}
                sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.75rem' }}
                onClick={() => history.push(`../../consultants/${record?.consultantId}/view`)}
              >
                Preview
              </Button>
            );
          },
        },
      },
    ];

    return (
      <Paper
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '12px',
          overflow: 'hidden',
        }}
      >
        <MUIDataTable
          data={consultantList}
          columns={columns}
          options={{
            elevation: 0,
            search: false,
            filter: false,

            download: false,
            print: false,
            viewColumns: false,
            selectableRows: 'none',
            responsive: 'standard',
            pagination: false,
            fixedHeader: true,
            tableBodyHeight: '65vh',
            setRowProps: (ev, dataIndex) => ({
              style: { backgroundColor: dataIndex % 2 === 0 ? '#ffffff' : alpha('#f5f7ff', 0.5) },
            }),
            setTableProps: () => ({ size: 'small' }),
          }}
        />
      </Paper>
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', mb: 2.5, alignItems: 'center', gap: 2 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6" fontWeight={700} color="text.primary" sx={{ lineHeight: 1.2 }}>
            Accredited Consultants
          </Typography>
          <Typography variant="body2" color="textSecondary">
            External firms authorized to manage monitoring reports
          </Typography>
        </Box>
        {userCategory === userCats.PERSONNEL && (
          <Button
            variant="contained"
            disableElevation
            startIcon={<Add />}
            sx={{ textTransform: 'none', borderRadius: '8px', fontWeight: 700 }}
            onClick={() => setDialogInvite(true)}
          >
            Invite Consultant
          </Button>
        )}
      </Box>

      {consultantLoading ? (
        <Box sx={{ p: 2 }}>
          <LoaderSkeleton />
          <Divider sx={{ my: 2 }} />
          <LoaderSkeleton />
        </Box>
      ) : consultantList?.length < 1 ? (
        <Box
          sx={{ py: 8, textAlign: 'center', bgcolor: alpha('#f4f6f8', 0.5), borderRadius: '12px' }}
        >
          <Empty description="No consultants linked to this facility" />
        </Box>
      ) : (
        renderContent()
      )}

      {/* Action Menu for Personnel */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            mt: 1,
            width: 220,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            border: '1px solid',
            borderColor: 'divider',
          },
        }}
      >
        <MenuItem
          sx={{ py: 1.2 }}
          onClick={() => {
            handleMenuClose();
            history.push(`../../consultants/${consultantSelected?.consultantId}/view`);
          }}
        >
          <ListItemIcon>
            <VisibilityRounded fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="View Profile"
            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          />
        </MenuItem>

        {userCategory === userCats.PERSONNEL &&
          consultantSelected?.status === 'PENDING' &&
          consultantSelected?.inducedBy === 'CONSULTANT' && [
            <Divider key="div" />,
            <MenuItem
              key="handle"
              sx={{ py: 1.2 }}
              onClick={() => {
                handleMenuClose();
                setDialogRequest(true);
              }}
            >
              <ListItemIcon>
                <ApprovalRounded fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Handle Request"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 700, color: 'primary' }}
              />
            </MenuItem>,
          ]}
      </Menu>

      {/* Conditional Dialogs */}
      {dialogInvite && (
        <ConsultantInvite
          open={dialogInvite}
          onClose={() => {
            setDialogInvite(false);
            setRefresh(true);
          }}
          facilityId={facilityId}
        />
      )}
      {dialogRemove && (
        <ConsultantRemove
          open={dialogRemove}
          onClose={() => {
            setDialogRemove(false);
            setRefresh(true);
          }}
          facilityId={facilityId}
          consultant={consultantSelected}
        />
      )}
      {dialogRequest && (
        <ConsultantHandleRequest
          open={dialogRequest}
          onClose={() => {
            setDialogRequest(false);
            setRefresh(true);
          }}
          facilityId={facilityId}
          consultant={consultantSelected}
        />
      )}
    </Box>
  );
}
